import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useEntries, useLanguage } from '../api/hooks'
import type { Entry } from '../api/types'
import { Button, Card, EmptyState, Spinner } from '../components/ui'
import { beat } from '../lib/sound'
import { speak, stopSpeaking } from '../lib/speech'

/** Beats-per-minute presets. Slow enough to sing along with, by default. */
const TEMPOS = [
  { label: 'Slow', bpm: 50 },
  { label: 'Steady', bpm: 75 },
  { label: 'Brisk', bpm: 100 },
]

type Style = 'listen' | 'call'

export default function SingAlong() {
  const languageId = Number(useParams().id)
  const { data: language } = useLanguage(languageId)
  const { data: page, isPending } = useEntries(languageId, '')

  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [bpm, setBpm] = useState(75)
  const [loop, setLoop] = useState(true)
  const [style, setStyle] = useState<Style>('listen')
  const [phase, setPhase] = useState<'word' | 'gap'>('word')

  // Memoised: a fresh array each render would rebuild the playback callback
  // below on every tick and restart the song under the singer.
  const words: Entry[] = useMemo(() => page?.items ?? [], [page])
  const locale = language?.pronunciation_locale ?? null

  // Held in a ref as well as state: the timeout chain below is scheduled once
  // and must read the *current* values, not the ones captured when it started.
  const playingRef = useRef(playing)
  const indexRef = useRef(0)
  const timer = useRef<number | null>(null)
  playingRef.current = playing

  const stop = useCallback(() => {
    setPlaying(false)
    playingRef.current = false
    stopSpeaking()
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
    setPhase('word')
  }, [])

  // Stop when leaving the page: speech synthesis outlives the component and
  // would otherwise keep talking over the next screen.
  useEffect(() => stop, [stop])

  const step = useCallback(() => {
    if (!playingRef.current) return

    const index = indexRef.current
    const word = words[index]
    if (!word) {
      if (loop && words.length > 0) {
        indexRef.current = 0
        setCurrent(0)
        timer.current = window.setTimeout(step, 400)
      } else {
        stop()
      }
      return
    }

    const beatMs = (60 / bpm) * 1000
    beat(index % 4 === 0)
    setPhase('word')

    speak(word.target_text, {
      locale,
      onEnd: () => {
        if (!playingRef.current) return

        // Call-and-response leaves a silent bar for the singer, then repeats
        // the word so they can hear whether they had it right.
        if (style === 'call') {
          setPhase('gap')
          timer.current = window.setTimeout(() => {
            if (!playingRef.current) return
            speak(word.target_text, {
              locale,
              onEnd: () => {
                if (!playingRef.current) return
                advance(beatMs)
              },
            })
          }, beatMs * 2)
        } else {
          advance(beatMs)
        }
      },
    })

    function advance(gap: number) {
      indexRef.current += 1
      setCurrent(indexRef.current)
      timer.current = window.setTimeout(step, gap)
    }
  }, [bpm, locale, loop, stop, style, words])

  function start(from = 0) {
    if (!locale) return
    indexRef.current = from
    setCurrent(from)
    setPlaying(true)
    playingRef.current = true
    // Scheduled rather than called, so `playing` has committed first.
    timer.current = window.setTimeout(step, 50)
  }

  if (isPending) return <Spinner />

  if (!locale) {
    return (
      <EmptyState
        title="No voice chosen yet"
        hint="Sing-along reads your words aloud, so it needs a pronunciation voice. Pick one on the language page and this comes to life."
        action={
          <Link to={`/languages/${languageId}`}>
            <Button>Choose a voice</Button>
          </Link>
        }
      />
    )
  }

  if (words.length === 0) {
    return (
      <EmptyState
        title="Nothing to sing"
        hint="Add some words and they become the lyrics."
        action={
          <Link to={`/languages/${languageId}`}>
            <Button>Add words</Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <div className="flex items-center justify-between">
        <Link
          to={`/languages/${languageId}/games`}
          className="text-sm text-muted hover:text-ink"
        >
          ← Games
        </Link>
        <p className="text-sm text-muted">
          {playing ? `${current + 1} / ${words.length}` : `${words.length} words`}
        </p>
      </div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Sing along</h1>
        <p className="text-sm text-muted">
          {style === 'listen'
            ? 'Listen and sing with it.'
            : 'It sings, you answer in the gap, then it sings again.'}
        </p>
      </div>

      {/* The lyrics. The active line is large and centred; neighbours fade out
          so the eye can find its place without the list jumping. */}
      <Card className="max-h-96 space-y-1 overflow-y-auto py-6">
        {words.map((word, index) => {
          const active = playing && index === current
          const distance = Math.abs(index - current)
          return (
            <button
              key={word.id}
              onClick={() => start(index)}
              className={`block w-full rounded-lg px-3 py-1.5 text-center transition-all ${
                active
                  ? 'scale-105 bg-brand-soft text-brand'
                  : distance <= 2
                    ? 'opacity-70'
                    : 'opacity-35'
              }`}
            >
              <span
                className={`font-semibold ${active ? 'text-2xl' : 'text-base'}`}
              >
                {word.target_text}
              </span>
              <span className="ml-2 text-sm text-muted">{word.source_text}</span>
              {active && phase === 'gap' && (
                <span className="ml-2 animate-pulse text-sm">🎤 your turn</span>
              )}
            </button>
          )
        })}
      </Card>

      <Card className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {playing ? (
            <Button onClick={stop} className="flex-1">
              Stop
            </Button>
          ) : (
            <Button onClick={() => start(0)} className="flex-1">
              ▶ Play from the top
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">Tempo</span>
          {TEMPOS.map((tempo) => (
            <Button
              key={tempo.bpm}
              variant={bpm === tempo.bpm ? 'primary' : 'secondary'}
              onClick={() => setBpm(tempo.bpm)}
            >
              {tempo.label}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">Mode</span>
          <Button
            variant={style === 'listen' ? 'primary' : 'secondary'}
            onClick={() => setStyle('listen')}
          >
            Listen
          </Button>
          <Button
            variant={style === 'call' ? 'primary' : 'secondary'}
            onClick={() => setStyle('call')}
          >
            Call and response
          </Button>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={loop}
            onChange={(e) => setLoop(e.target.checked)}
            className="size-4 accent-[var(--color-brand)]"
          />
          Loop back to the start
        </label>

        <p className="text-xs text-muted">
          Tap any line to start from there. Voices come from your browser, so the
          accent depends on which ones it has installed.
        </p>
      </Card>
    </div>
  )
}
